import Button from 'react-bootstrap/Button';
import { ButtonGroup, Form } from 'react-bootstrap';
import React, { FC, useContext, useEffect, useState } from 'react';
import { IDistribution } from '../../../model/distributions/Distribution';
import RootStoreContext from '../../../model/RootStoreContext';
import { useNavigate } from 'react-router';
import { IGameVersion } from '../../../model/gameVersion/GameVersion';
import { IGameEnvironment } from '../../../model/gameEnvironment/GameEnvironment';
import { useGetDistributionDiffLink } from '../../routes';
import { observer } from 'mobx-react-lite';
import { useShowModal } from '@yakoffice/custom-modal';
import styles from './compare-distribution-buttons.module.sass';
import { GameEnvironmentSelectorAsSelection } from '../common/GameEnvironmentSelectorAsSelection';
import { GameVersionSelectorAsSelection } from '../common/GameVersionSelectorAsSelection';

interface PropsType {
    distributions: IDistribution[];
    checkedDistributions: IDistribution[];
    handleComparePublishedWithLastDistribution: (diffGameVersion: IGameVersion, diffGameEnvironment: IGameEnvironment, rightDiff: boolean) => void;
}

export const CompareDistributionsButton: FC<PropsType> = observer(({
                                                                       distributions,
                                                                       checkedDistributions,
                                                                       handleComparePublishedWithLastDistribution
                                                                   }) => {
    const rootStore = useContext(RootStoreContext);
    const navigate = useNavigate();
    const getDistributionDiffLink = useGetDistributionDiffLink();
    const [checkedDistribution, setCheckedDistribution] = useState<number>(checkedDistributions[0] ? checkedDistributions[0].id : distributions[0].id);
    const [secondCheckedDistribution, setSecondCheckedDistribution] = useState<number>(checkedDistributions[1] && checkedDistributions[1].id);
    const [rightDiff, setRightDiff] = useState(true);
    const [diffGameVersion, setDiffGameVersion] = useState(rootStore.gameVersionStore.getCurrentGameVersion());
    const [diffGameEnvironment, setDiffGameEnvironment] = useState(rootStore.gameEnvironmentsStore.getCurrentGameEnvironment());
    const showModal = useShowModal();

    useEffect(() => {
        setDiffGameEnvironment(rootStore.gameEnvironmentsStore.getCurrentGameEnvironment());
    }, [rootStore.gameEnvironmentsStore, rootStore.gameEnvironmentsStore.currentGameEnvironment]);

    const handleCompare = () => {
        showModal({ body: '', canClose: false, title: '', show: false });

        const currentGameVersion = rootStore.gameVersionStore.getCurrentGameVersion();
        const currentGameEnvironment = rootStore.gameEnvironmentsStore.getCurrentGameEnvironment();

        if (checkedDistributions.length === 0) handleComparePublishedWithLastDistribution(diffGameVersion, diffGameEnvironment, rightDiff);

        if (checkedDistribution && !secondCheckedDistribution) {
            navigate(
                getDistributionDiffLink({
                    gameVersionId: rightDiff ? currentGameVersion.id : diffGameVersion.id,
                    gameEnvironmentId: rightDiff ? currentGameEnvironment.id : diffGameEnvironment.id,
                    distributionId: 'published',
                    diffGameVersionId: rightDiff ? diffGameVersion.id : currentGameVersion.id,
                    diffGameEnvironmentId: rightDiff ? diffGameEnvironment.id : currentGameEnvironment.id,
                    diffDistributionId: checkedDistribution
                })
            );
        } else if (checkedDistribution && secondCheckedDistribution) {
            navigate(
                getDistributionDiffLink({
                    gameVersionId: currentGameVersion.id,
                    gameEnvironmentId: currentGameEnvironment.id,
                    distributionId: rightDiff ? checkedDistribution : secondCheckedDistribution,
                    diffGameVersionId: currentGameVersion.id,
                    diffGameEnvironmentId: currentGameEnvironment.id,
                    diffDistributionId: rightDiff ? secondCheckedDistribution : checkedDistribution
                })
            );
        }
    };

    return (
        <div className={styles.compareDistributionButtons}>
            <div className={styles.comparisonRow}>
                <Form.Group className={styles.formGroup}>
                    <Form.Label>Distribution 1</Form.Label>
                    <Form.Select
                        className="form-control"
                        defaultValue={checkedDistributions[0] && checkedDistributions[0].id}
                        onChange={(e) => setCheckedDistribution(Number.parseInt(e.target.value))}
                    >
                        {distributions.map((distribution, index) => (
                            <option key={distribution.id} value={distribution.id}>
                                {index == 0 ? `${distribution.id} (Latest)` : distribution.id}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>

                <Form.Group className={styles.formGroup}>
                    <Form.Label>Distribution 2</Form.Label>
                    <Form.Select
                        className="form-control"
                        defaultValue={checkedDistributions[1] && checkedDistributions[1].id}
                        onChange={(e) => setSecondCheckedDistribution(Number.parseInt(e.target.value))}
                    >
                        <option value={undefined}>All published</option>
                        {distributions
                            .filter((distribution) => distribution.id !== checkedDistribution)
                            .map((distribution, index) => (
                                <option key={distribution.id} value={distribution.id}>
                                    {distribution.id}
                                </option>
                            ))}
                    </Form.Select>
                </Form.Group>
            </div>
            {!secondCheckedDistribution && (
                <>
                    <strong>All published in: </strong>
                    <div className={`${styles.comparisonRow} mt-3`}>
                        <Form.Group className={styles.formGroup}>
                            <Form.Label>Game Environment</Form.Label>
                            <GameEnvironmentSelectorAsSelection
                                as={ButtonGroup}
                                className="ms-0"
                                selectedGameEnvironment={diffGameEnvironment}
                                handleGameEnvironmentChange={(ge) => setDiffGameEnvironment(ge)}
                                data-testid="ddlCompareGe"
                            />
                        </Form.Group>
                        <Form.Group className={styles.formGroup}>
                            <Form.Label>Game Environment</Form.Label>
                            <GameVersionSelectorAsSelection
                                as={ButtonGroup}
                                selectedGameVersion={diffGameVersion}
                                handleGameVersionChange={(gv) => setDiffGameVersion(gv)}
                                data-testid="ddlCompareGv"
                            />
                        </Form.Group>
                    </div>
                </>
            )}
            <div className="float-end fakeFooter">
                <Button onClick={() => showModal({ body: '', canClose: false, title: '', show: false })} variant="link"
                        data-testid="btnModalCancelClose" className={styles.closeButton}>
                    Close
                </Button>
                <Button variant="primary" onClick={handleCompare} data-testid="btnCompare">
                    Compare
                </Button>
            </div>
        </div>
    );
});
